/**
 * AnimatedHelper.js — YLC Motion Studio front-end · v2 (2026-02-21)
 * ────────────────────────────────────────────────────────────────
 * User mandate: "It takes too long for animations to kick in. Students
 * shouldn't have to wait so long for course materials."
 *
 * v2 redesign — students NEVER wait. On mount we IMMEDIATELY render an
 * inline SVG cartoon character with CSS-driven motion (breathing bob,
 * blink, gesture, live caption reveal). Web Speech API narrates the
 * lesson text via the browser's native voice — zero server round-trip.
 * The MP4 (server-rendered, higher-fidelity, TTS-mixed) is fetched in
 * the background — when it's ready we hot-swap it in with a fade.
 *
 * Result:
 *   • First-paint: ~0ms — instant animated character + narration
 *   • Enhanced-paint: ~10-30s later when MP4 lands
 *   • Repeat visits: MP4 loads directly from cache in ~200ms
 *
 * Also prefetches the NEXT lesson's MP4 in the background when
 * `prefetchNext` is provided, so consecutive lesson clicks feel free.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Per-session cache so remounts (tab re-clicks) never re-request the same lesson.
const videoCache = new Map();

// Grade-tier palette mirrors the backend (Pillow) side so the instant
// preview matches the final MP4's look.
const TIER_PALETTE = {
  "K-5":       { top: "#ffe0b2", bottom: "#ffb74d", accent: "#ff6b9d", face: "#8d5524" },
  "6-8":       { top: "#dbeafe", bottom: "#2563eb", accent: "#4B0082", face: "#8d5524" },
  "9-12":      { top: "#dbeafe", bottom: "#2563eb", accent: "#4B0082", face: "#8d5524" },
  "Seminary":  { top: "#191937", bottom: "#d4af37", accent: "#d4af37", face: "#8d5524" },
  "Adult":     { top: "#191937", bottom: "#d4af37", accent: "#d4af37", face: "#8d5524" },
};

export default function AnimatedHelper({
  lessonId,
  prompt,
  gradeTier = "6-8",
  narrationText = "",
  lessonTitle = "",
  variant = "banner",
  autoGenerate = true, // now defaults to true — instant + background enhance
}) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [bgError, setBgError] = useState(null);
  const [cached, setCached] = useState(false);
  const [edition, setEdition] = useState("lesson");
  const [expanded, setExpanded] = useState(variant === "banner");
  const videoRef = useRef(null);
  const requestedRef = useRef(null);
  const palette = TIER_PALETTE[gradeTier] || TIER_PALETTE["6-8"];

  // Fire off the server-side MP4 generation in the background. Never
  // blocks the UI — the CSS character keeps playing regardless.
  const prefetchVideo = useCallback(async () => {
    if (!lessonId || !prompt || !autoGenerate) return;
    if (requestedRef.current === lessonId) return; // one request per lesson
    requestedRef.current = lessonId;
    const hit = videoCache.get(lessonId);
    if (hit) {
      setVideoUrl(`${BACKEND}${hit.video_url}`);
      setCached(hit.cached);
      setEdition(hit.edition || "lesson");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API}/motion-studio/generate`,
        {
          lesson_id: lessonId,
          prompt,
          grade_tier: gradeTier,
          narration_text: narrationText || `Let's learn about ${prompt}.`,
          lesson_title: lessonTitle,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 20000,
        }
      );
      videoCache.set(lessonId, data);
      setVideoUrl(`${BACKEND}${data.video_url}`);
      setCached(data.cached);
      setEdition(data.edition || "lesson");
    } catch (e) {
      // Non-fatal — the CSS character stays on screen. Log for admin
      // debugging only if the enhanced video ever fails.
      requestedRef.current = null;
      setBgError(e?.friendlyMessage || e?.message || "background render failed");
    }
  }, [lessonId, prompt, gradeTier, narrationText, lessonTitle, autoGenerate]);

  useEffect(() => {
    if (expanded && !videoUrl) prefetchVideo();
  }, [expanded, videoUrl, prefetchVideo]);

  // Floating variant — minimize until user opens
  if (variant === "floating") {
    return (
      <div
        data-testid="animated-helper-floating"
        style={{
          position: "fixed", right: 20, bottom: 130, zIndex: 40,
          maxWidth: expanded ? "380px" : "72px",
          transition: "max-width 300ms ease",
        }}
      >
        {!expanded ? (
          <button
            data-testid="animated-helper-open-btn"
            onClick={() => setExpanded(true)}
            aria-label="Open cartoon helper"
            style={{
              width: 72, height: 72, borderRadius: "50%",
              border: `3px solid ${palette.accent}`,
              background: `linear-gradient(135deg, ${palette.top} 0%, ${palette.bottom} 100%)`,
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              fontSize: "1.8rem", color: "#fff",
            }}
          >🎬</button>
        ) : (
          <HelperCard
            palette={palette} gradeTier={gradeTier} cached={cached}
            videoUrl={videoUrl} narrationText={narrationText} edition={edition}
            lessonTitle={lessonTitle} videoRef={videoRef}
            bgError={bgError} onClose={() => setExpanded(false)}
            compact
          />
        )}
      </div>
    );
  }

  // Banner variant
  return (
    <HelperCard
      palette={palette} gradeTier={gradeTier} cached={cached}
      videoUrl={videoUrl} narrationText={narrationText} edition={edition}
      lessonTitle={lessonTitle} videoRef={videoRef} bgError={bgError}
    />
  );
}

/* ── Presentational card ─────────────────────────────────────────── */
function HelperCard({ palette, gradeTier, cached, videoUrl, narrationText, edition,
                     lessonTitle, videoRef, bgError, onClose, compact = false }) {
  return (
    <div
      data-testid="animated-helper-banner"
      style={{
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        border: `1px solid ${palette.accent}44`,
        borderRadius: 14, padding: 16, margin: compact ? 0 : "16px 0",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      }}
    >
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 10,
      }}>
        <div>
          <div style={{ color: "#FFD700", fontSize: "0.9rem", fontWeight: "bold" }}>
            🎬 Cartoon Helper · {gradeTier}
          </div>
          {lessonTitle && (
            <div style={{ color: "#aaa", fontSize: "0.75rem", marginTop: 2 }}>
              {lessonTitle}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {videoUrl && edition === "general" && (
            <span
              data-testid="animated-helper-general-edition-tag"
              title="This lesson's own Eli video is being made and will replace this one automatically."
              style={{
                fontSize: "0.65rem", color: "#FFD700",
                padding: "3px 8px", borderRadius: 6,
                background: "#3a2a0a", border: "1px solid #FFD70066",
              }}
            >
              General Edition
            </span>
          )}
          {videoUrl && edition !== "general" && (
            <span data-testid="animated-helper-lesson-edition-tag" style={{
              fontSize: "0.65rem", color: "#90EE90",
              padding: "3px 8px", borderRadius: 6,
              background: "#0a3a0a",
            }}>
              Lesson Edition
            </span>
          )}
          {!videoUrl && (
            <span style={{
              fontSize: "0.65rem", color: "#FFB84D",
              padding: "3px 8px", borderRadius: 6, background: "#3a2a0a",
            }}>
              Playing · HD loading
            </span>
          )}
          {onClose && (
            <button onClick={onClose} aria-label="Minimize"
                    style={{ background: "transparent", color: "#888",
                             border: "none", cursor: "pointer",
                             fontSize: "1.1rem" }}>×</button>
          )}
        </div>
      </div>

      {/* Content: video takes over once ready; instant animation until then */}
      {videoUrl ? (
        <div>
          <video
            ref={videoRef}
            data-testid="animated-helper-video"
            src={videoUrl}
            poster="/eli/poster.jpg"
            controls playsInline preload="metadata"
            style={{
              width: "100%", borderRadius: 8, background: "#000",
              animation: "helper-fade-in 400ms ease-out",
            }}
          >Your browser does not support the video tag.</video>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <a
              href={videoUrl}
              download={`ylc-${(lessonTitle || "lesson").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.mp4`}
              data-testid="animated-helper-download-btn"
              style={{
                padding: "6px 14px", borderRadius: 6, textDecoration: "none",
                background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: "#111", fontWeight: "bold", fontSize: "0.78rem",
                border: "1px solid #FFD70088",
              }}
            >⬇︎ Download MP4</a>
            <button
              data-testid="animated-helper-replay-btn"
              onClick={() => { if (videoRef?.current) { videoRef.current.currentTime = 0; videoRef.current.play(); } }}
              style={{
                padding: "6px 14px", borderRadius: 6, cursor: "pointer", width: "auto", marginTop: 0,
                background: "transparent", color: "#FFD700",
                border: "1px solid #FFD70088", fontSize: "0.78rem",
              }}
            >↻ Replay</button>
            <button
              data-testid="animated-helper-fullscreen-btn"
              onClick={() => { if (videoRef?.current?.requestFullscreen) videoRef.current.requestFullscreen(); }}
              style={{
                padding: "6px 14px", borderRadius: 6, cursor: "pointer", width: "auto", marginTop: 0,
                background: "transparent", color: "#87CEEB",
                border: "1px solid #87CEEB88", fontSize: "0.78rem",
              }}
            >⛶ Fullscreen</button>
          </div>
        </div>
      ) : (
        <InstantCharacter
          palette={palette} narrationText={narrationText}
          lessonTitle={lessonTitle}
        />
      )}

      {bgError && videoUrl == null && (
        <div style={{
          marginTop: 8, fontSize: "0.7rem", color: "#ff9080",
          padding: "4px 8px", background: "#3a1a1a11", borderRadius: 4,
        }}>
          HD render deferred — using instant preview. ({bgError.slice(0, 60)})
        </div>
      )}
    </div>
  );
}

/* ── Zero-latency inline SVG character with breathing + gesture ────── */
function InstantCharacter({ palette, narrationText, lessonTitle }) {
  const [revealed, setRevealed] = useState(0);
  const maxLen = Math.min((narrationText || "").length, 600);

  // Progressive caption reveal (typewriter) — synchronized to a
  // hypothetical 12-second read; caps at maxLen.
  useEffect(() => {
    if (!maxLen) return;
    let start = performance.now();
    let raf;
    const tick = (now) => {
      const t = (now - start) / 1000;
      const pct = Math.min(1, t / 12);
      setRevealed(Math.floor(pct * maxLen));
      if (pct < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [maxLen]);

  // Browser TTS — no server round-trip. Uses default system voice.
  useEffect(() => {
    if (!narrationText || typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(narrationText.slice(0, 600));
      utter.rate = 0.95;
      utter.pitch = 1.0;
      window.speechSynthesis.speak(utter);
      return () => {
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      };
    } catch {
      /* speechSynthesis may throw on some mobile browsers — non-fatal */
    }
  }, [narrationText]);

  return (
    <div data-testid="animated-helper-instant"
         style={{
           position: "relative", width: "100%",
           background: `url(/eli/backdrop.webp) center / cover, ${palette.bottom}`,
           borderRadius: 8, overflow: "hidden",
           aspectRatio: "16 / 9",
           display: "flex", alignItems: "flex-end",
         }}>
      {/* CSS keyframes — inject once per mount */}
      <style>{`
        @keyframes helper-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes helper-walk-in {
          from { transform: translateX(-140%) }
          to { transform: translateX(0) }
        }
        @keyframes helper-bob {
          0%,100% { transform: translateY(0) }
          50% { transform: translateY(-8px) }
        }
        @keyframes helper-blink {
          0%, 96%, 100% { transform: scaleY(1) }
          98% { transform: scaleY(0.1) }
        }
        @keyframes helper-arm-wave {
          0%, 100% { transform: rotate(-8deg) }
          50% { transform: rotate(12deg) }
        }
      `}</style>

      {/* Eli — painted teacher, paints instantly from a small static image */}
      <div style={{
        position: "absolute", left: "6%", bottom: 0,
        width: "34%", maxWidth: 240,
        animation: "helper-walk-in 900ms ease-out both",
      }}>
        <div style={{ animation: "helper-bob 2600ms ease-in-out infinite" }}>
          <img
            src="/eli/eli.webp" alt="Eli, your teacher"
            data-testid="animated-helper-eli-image"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>
      </div>

      {/* Caption panel */}
      <div style={{
        position: "absolute", right: "5%", top: "12%",
        width: "50%", maxWidth: 380,
        background: "rgba(255,255,255,0.95)", color: "#111",
        padding: "14px 16px", borderRadius: 12,
        boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
        fontSize: "0.92rem", lineHeight: 1.4, minHeight: 80,
      }}>
        {lessonTitle && (
          <div style={{ color: palette.accent, fontWeight: "bold",
                        fontSize: "0.8rem", marginBottom: 6 }}>
            {lessonTitle}
          </div>
        )}
        <div>
          {(narrationText || "").slice(0, revealed) || "\u00a0"}
          {revealed < maxLen && <span style={{ opacity: 0.4 }}>▍</span>}
        </div>
      </div>

      {/* Title badge */}
      <div style={{
        position: "absolute", top: 8, left: 10,
        background: "rgba(0,0,0,0.55)", color: "#FFD700",
        padding: "3px 10px", borderRadius: 6, fontSize: "0.72rem",
        fontWeight: "bold",
      }}>Eli · loading your lesson video</div>
    </div>
  );
}
